(() => {
  if (customElements.get("search-prompt-rotator")) return;

  const DISPLAY_DURATION = 2200;
  const HIDE_DURATION = 300;
  const SHOW_DURATION = 700;
  const GRADIENT_COUNT = 5;
  const MAX_ROTATIONS = 3;
  const SCROLL_SETTLE_THRESHOLD = 80;

  class SearchPromptRotator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.field = this.closest(".field");
      this.input = this.field?.querySelector('input[type="search"]');
      this.term = this.querySelector("[data-search-prompt-term]");
      this.options = Array.from(
        this.querySelectorAll("[data-search-prompt-option]"),
      )
        .map((option) => option.textContent.trim())
        .filter(Boolean);
      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.currentIndex = 0;
      this.completedRotations = 0;
      this.hasSettled = window.scrollY > SCROLL_SETTLE_THRESHOLD;

      if (!this.field || !this.input || !this.term || !this.options.length)
        return;

      this.initialized = true;
      this.renderCurrentTerm();

      this.onInputStateChange = this.update.bind(this);
      this.onMotionPreferenceChange = this.update.bind(this);
      this.onVisibilityChange = this.update.bind(this);
      this.onScroll = this.settleAfterScroll.bind(this);

      this.input.addEventListener("focus", this.onInputStateChange);
      this.input.addEventListener("blur", this.onInputStateChange);
      this.input.addEventListener("input", this.onInputStateChange);
      this.input.addEventListener("change", this.onInputStateChange);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      window.addEventListener("scroll", this.onScroll, { passive: true });

      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener(
          "change",
          this.onMotionPreferenceChange,
        );
      } else {
        this.reducedMotion.addListener(this.onMotionPreferenceChange);
      }

      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.clearTimers();
      this.field.classList.remove("search-prompt-active");
      this.input.removeEventListener("focus", this.onInputStateChange);
      this.input.removeEventListener("blur", this.onInputStateChange);
      this.input.removeEventListener("input", this.onInputStateChange);
      this.input.removeEventListener("change", this.onInputStateChange);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      window.removeEventListener("scroll", this.onScroll);

      if (this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener(
          "change",
          this.onMotionPreferenceChange,
        );
      } else {
        this.reducedMotion.removeListener(this.onMotionPreferenceChange);
      }

      this.initialized = false;
    }

    update() {
      const shouldShow =
        !document.hidden &&
        document.activeElement !== this.input &&
        !this.input.value.trim();

      if (!shouldShow) {
        this.hidePrompt();
        return;
      }

      this.hidden = false;
      this.field.classList.add("search-prompt-active");

      if (
        this.options.length > 1 &&
        !this.reducedMotion.matches &&
        !this.hasSettled &&
        this.completedRotations < MAX_ROTATIONS
      ) {
        this.scheduleNextTerm();
      } else {
        this.clearTimers();
        delete this.term.dataset.state;
      }
    }

    hidePrompt() {
      this.hidden = true;
      this.field.classList.remove("search-prompt-active");
      delete this.term.dataset.state;
      this.clearTimers();
    }

    settleAfterScroll() {
      if (this.hasSettled || window.scrollY <= SCROLL_SETTLE_THRESHOLD) return;

      this.hasSettled = true;
      delete this.term.dataset.state;
      this.clearTimers();
    }

    renderCurrentTerm() {
      this.term.textContent = this.options[this.currentIndex];
      this.dataset.gradient = String((this.currentIndex % GRADIENT_COUNT) + 1);
    }

    scheduleNextTerm() {
      if (
        this.hasSettled ||
        this.completedRotations >= MAX_ROTATIONS ||
        this.displayTimer ||
        this.swapTimer ||
        this.revealTimer
      )
        return;

      this.displayTimer = window.setTimeout(() => {
        this.displayTimer = null;
        this.term.dataset.state = "hiding";

        this.swapTimer = window.setTimeout(() => {
          this.swapTimer = null;
          this.currentIndex = (this.currentIndex + 1) % this.options.length;
          this.completedRotations += 1;
          this.renderCurrentTerm();
          this.term.dataset.state = "revealing";

          this.revealTimer = window.setTimeout(() => {
            this.revealTimer = null;
            delete this.term.dataset.state;
            this.scheduleNextTerm();
          }, SHOW_DURATION);
        }, HIDE_DURATION);
      }, DISPLAY_DURATION);
    }

    clearTimers() {
      window.clearTimeout(this.displayTimer);
      window.clearTimeout(this.swapTimer);
      window.clearTimeout(this.revealTimer);
      this.displayTimer = null;
      this.swapTimer = null;
      this.revealTimer = null;
    }
  }

  customElements.define("search-prompt-rotator", SearchPromptRotator);
})();
